import { useEffect, useState } from "react";
import { supabase } from "../lib/supabaseClient";

// ============================================================
// ADMIN CREDENTIALS — Change these to your own
// ============================================================
const ADMIN_USERNAME = import.meta.env.VITE_ADMIN_USERNAME;
const ADMIN_PASSWORD = import.meta.env.VITE_ADMIN_PASSWORD;
// ============================================================

function AdminLogin({ onSuccess }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
  e.preventDefault();
  setLoading(true);
  try {
    const res = await fetch("/api/admin-login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username, password }),
    });
    if (res.ok) {
      sessionStorage.setItem("admin_auth", "true");
      onSuccess();
    } else {
      setError(true);
      setPassword("");
      setTimeout(() => setError(false), 3000);
    }
  } catch {
    setError(true);
  } finally {
    setLoading(false);
  }
};

  return (
    <div className="min-h-screen bg-[#020617] flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="text-4xl mb-4">🔐</div>
          <h1 className="text-2xl font-black uppercase italic tracking-tighter text-white">Admin Console</h1>
          <p className="text-red-500 text-[10px] font-black uppercase tracking-widest mt-2">Digitoly QR — Restricted Access</p>
        </div>

        <form onSubmit={handleLogin} className="bg-slate-900/50 border border-white/10 rounded-3xl p-8 space-y-4">
          <div>
            <label className="text-[9px] font-black uppercase text-gray-500 block mb-2">Username</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Enter username"
              className="w-full p-4 bg-white/5 border border-white/10 rounded-xl text-white outline-none focus:border-red-500 transition-all text-sm"
              autoFocus
            />
          </div>
          <div>
            <label className="text-[9px] font-black uppercase text-gray-500 block mb-2">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password"
              className={`w-full p-4 bg-white/5 border rounded-xl text-white outline-none transition-all text-sm ${error ? "border-red-500" : "border-white/10 focus:border-red-500"}`}
            />
          </div>

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3 text-center">
              <p className="text-red-400 text-[10px] font-black uppercase">Invalid credentials</p>
            </div>
          )}

          <button
            type="submit"
            disabled={loading || !username || !password}
            className="w-full p-4 bg-red-600 hover:bg-red-500 disabled:opacity-50 disabled:cursor-not-allowed rounded-2xl text-white font-black uppercase tracking-widest transition-all"
          >
            {loading ? "Verifying..." : "Access Console"}
          </button>
        </form>

        <p className="text-center text-gray-700 text-[9px] uppercase tracking-widest mt-6">
          Unauthorized access is prohibited
        </p>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const [authenticated, setAuthenticated] = useState(
    sessionStorage.getItem("admin_auth") === "true"
  );
  const [stats, setStats] = useState({
    totalQRs: 0,
    totalScans: 0,
    totalLeads: 0,
    totalFeedback: 0,
    proUsers: 0,
    agencyUsers: 0,
    monthlyRevenue: 0,
    yearlyRevenue: 0,
  });
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authenticated) fetchAdminData();
  }, [authenticated]);

  const fetchAdminData = async () => {
    setLoading(true);
    try {
      const [
        { count: totalQRs },
        { count: totalScans },
        { count: totalLeads },
        { count: totalFeedback },
        { data: subs }
      ] = await Promise.all([
        supabase.from("qr_codes").select("*", { count: "exact", head: true }),
        supabase.from("qr_logs").select("*", { count: "exact", head: true }),
        supabase.from("qr_leads").select("*", { count: "exact", head: true }),
        supabase.from("feedback").select("*", { count: "exact", head: true }),
        supabase.from("subscriptions").select("*").eq("status", "active").order("created_at", { ascending: false })
      ]);

      const proMonthly = subs?.filter(s => s.plan === "pro" && s.billing_cycle === "monthly").length || 0;
      const proYearly = subs?.filter(s => s.plan === "pro" && s.billing_cycle === "yearly").length || 0;
      const agencyMonthly = subs?.filter(s => s.plan === "agency" && s.billing_cycle === "monthly").length || 0;
      const agencyYearly = subs?.filter(s => s.plan === "agency" && s.billing_cycle === "yearly").length || 0;

      setStats({
        totalQRs: totalQRs || 0,
        totalScans: totalScans || 0,
        totalLeads: totalLeads || 0,
        totalFeedback: totalFeedback || 0,
        proUsers: proMonthly + proYearly,
        agencyUsers: agencyMonthly + agencyYearly,
        monthlyRevenue: (proMonthly * 499) + (agencyMonthly * 1499),
        yearlyRevenue: (proYearly * 4999) + (agencyYearly * 14999),
      });

      setSubscriptions(subs || []);
    } catch (err) {
      console.error("Admin fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem("admin_auth");
    setAuthenticated(false);
  };

  if (!authenticated) return <AdminLogin onSuccess={() => setAuthenticated(true)} />;

  const StatCard = ({ label, value, color = "blue", prefix = "" }) => (
    <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
      <p className="text-[10px] font-black uppercase text-slate-500 tracking-widest mb-2">{label}</p>
      <h2 className={`text-3xl font-black text-${color}-400`}>{prefix}{value?.toLocaleString()}</h2>
    </div>
  );

  if (loading) return (
    <div className="min-h-screen bg-[#020617] flex items-center justify-center">
      <div className="w-10 h-10 border-4 border-red-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="p-8 bg-[#020617] min-h-screen text-white">
      <div className="max-w-7xl mx-auto space-y-10">

        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-black uppercase italic tracking-tighter">Admin Console</h1>
            <p className="text-red-500 text-[10px] font-black uppercase tracking-widest mt-1">Digitoly QR — Internal Dashboard</p>
          </div>
          <div className="flex gap-3">
            <button onClick={fetchAdminData} className="bg-white/5 border border-white/10 px-6 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-white/10 transition-all">
              Refresh
            </button>
            <button onClick={handleLogout} className="bg-red-600/20 border border-red-500/30 text-red-400 px-6 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-red-600 hover:text-white transition-all">
              Logout
            </button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Total QR Codes" value={stats.totalQRs} color="blue" />
          <StatCard label="Total Scans" value={stats.totalScans} color="purple" />
          <StatCard label="Leads Captured" value={stats.totalLeads} color="green" />
          <StatCard label="Feedback Received" value={stats.totalFeedback} color="yellow" />
        </div>

        {/* Revenue */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-gradient-to-br from-blue-500/10 to-purple-500/10 border border-blue-500/20 rounded-2xl p-6">
            <p className="text-[10px] font-black uppercase text-slate-500 tracking-widest mb-2">Monthly Recurring Revenue</p>
            <h2 className="text-4xl font-black text-white">₹{stats.monthlyRevenue.toLocaleString()}</h2>
            <p className="text-[10px] text-slate-500 mt-2">{stats.proUsers} Pro + {stats.agencyUsers} Agency users</p>
          </div>
          <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/20 rounded-2xl p-6">
            <p className="text-[10px] font-black uppercase text-slate-500 tracking-widest mb-2">Annual Revenue (Yearly Plans)</p>
            <h2 className="text-4xl font-black text-white">₹{stats.yearlyRevenue.toLocaleString()}</h2>
            <p className="text-[10px] text-slate-500 mt-2">Locked-in revenue</p>
          </div>
          <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-2xl p-6">
            <p className="text-[10px] font-black uppercase text-slate-500 tracking-widest mb-2">Total Active Subscribers</p>
            <h2 className="text-4xl font-black text-white">{stats.proUsers + stats.agencyUsers}</h2>
            <p className="text-[10px] text-slate-500 mt-2">{stats.proUsers} Pro · {stats.agencyUsers} Agency</p>
          </div>
        </div>

        {/* Active Subscriptions Table */}
        <div className="bg-white/5 border border-white/10 rounded-[32px] overflow-hidden">
          <div className="p-6 border-b border-white/10">
            <h3 className="text-sm font-black uppercase tracking-widest">Active Subscriptions</h3>
          </div>
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-white/5">
                <th className="px-6 py-4 text-[10px] font-black uppercase text-slate-500 tracking-widest">User ID</th>
                <th className="px-6 py-4 text-[10px] font-black uppercase text-slate-500 tracking-widest">Plan</th>
                <th className="px-6 py-4 text-[10px] font-black uppercase text-slate-500 tracking-widest">Billing</th>
                <th className="px-6 py-4 text-[10px] font-black uppercase text-slate-500 tracking-widest">Started</th>
                <th className="px-6 py-4 text-[10px] font-black uppercase text-slate-500 tracking-widest">Expires</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {subscriptions.length === 0 ? (
                <tr><td colSpan="5" className="px-6 py-10 text-center text-slate-600 text-sm">No active subscriptions</td></tr>
              ) : subscriptions.map(sub => (
                <tr key={sub.id} className="hover:bg-white/5 transition-colors">
                  <td className="px-6 py-4 text-[10px] font-mono text-slate-400">{sub.user_id?.slice(0, 16)}...</td>
                  <td className="px-6 py-4">
                    <span className={`text-[10px] font-black uppercase px-3 py-1 rounded-full ${sub.plan === "agency" ? "bg-purple-500/20 text-purple-400" : "bg-blue-500/20 text-blue-400"}`}>
                      {sub.plan}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-[10px] text-slate-400 uppercase">{sub.billing_cycle}</td>
                  <td className="px-6 py-4 text-[10px] text-slate-400">{new Date(sub.started_at).toLocaleDateString()}</td>
                  <td className="px-6 py-4 text-[10px] text-slate-400">{sub.expires_at ? new Date(sub.expires_at).toLocaleDateString() : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

      </div>
    </div>
  );
}